import { useEffect, useRef, useState } from 'react'
import Card from '@mui/material/Card'
import Typography from '@mui/material/Typography'
import { Box } from '@mui/material'
import { Layer, MapRef, Source, ViewState } from 'react-map-gl'
import Map from './mapLayers/ClimateMap'
import ConfigProvider from 'balkerne-core/config'
import { useLocation } from '../hooks/locations'
import { useLocationAlerts } from '../hooks/alerts'
import bbox from '@turf/bbox'

const DashboardMap = ({ propertyId }) => {
  const { data: property } = useLocation(propertyId, { geojson: true })
  const { data: alerts } = useLocationAlerts(propertyId)
  const [showActiveAreas, setShowActiveAreas] = useState(true)
  const [showSatellite, setShowSatellite] = useState(false)
  const mapRef = useRef<MapRef | null>(null)

  useEffect(() => {
    if (mapRef.current && property) {
      mapRef.current.fitBounds(bbox(property.geometry) as [number, number, number, number], {
        duration: 2500,
        padding: 10,
        maxZoom: 18,
        pitch: 30,
      })
    }
  }, [property])

  const initialViewState: ViewState & { maxZoom?: number } = {
    longitude: property?.coordinates?.longitude ?? -1.5,
    latitude: property?.coordinates?.latitude ?? 52.5,
    padding: { left: 10, right: 10, top: 10, bottom: 10 },
    maxZoom: 18,
    zoom: 7,
    pitch: 30,
    bearing: 0,
  }
  return (
    <Card sx={{ minHeight: 'fit-content', position: 'relative' }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Risk Map
      </Typography>
      <Box>
        <Map
          reuseMaps
          ref={mapRef}
          initialViewState={initialViewState}
          style={{ height: '600px', width: '100%', zIndex: 0, borderRadius: 5 }}
          mapStyle={
            showSatellite ? 'mapbox://styles/mapbox/satellite-v9' : 'mapbox://styles/balkerne/cl9ydd2v700ic15mgaexvvi2j'
          }
          mapboxAccessToken={ConfigProvider.mapboxPublicKey}
          mapOverlays={[
            { id: 'alerts', label: 'Active alerts', checked: showActiveAreas, onChange: setShowActiveAreas },
            { id: 'satellite', label: 'Satellite', checked: showSatellite, onChange: setShowSatellite },
          ]}
        >
          {alerts?.map(alert => (
            <Source key={alert.id} type="geojson" data={alert.area.geometry}>
              {alert.area.type.point ? (
                <Layer
                  id={`alert-${alert.id}`}
                  type="circle"
                  paint={{ 'circle-color': '#2f20f7', 'circle-opacity': 0.75, 'circle-radius': 25 }}
                  layout={{ visibility: showActiveAreas ? 'visible' : 'none' }}
                />
              ) : (
                <Layer
                  id={`alert-${alert.id}`}
                  type="fill"
                  paint={{ 'fill-color': '#2f20f7', 'fill-opacity': 0.33 }}
                  layout={{ visibility: showActiveAreas ? 'visible' : 'none' }}
                />
              )}
            </Source>
          ))}
          {property?.geometry && (
            <Source type="geojson" data={{ type: 'Feature', geometry: property.geometry, properties: {} }}>
              <Layer
                id="property"
                type="fill-extrusion"
                paint={{
                  'fill-extrusion-color': '#34eb64',
                  'fill-extrusion-height': 5,
                  'fill-extrusion-opacity': 0.75,
                }}
              />
            </Source>
          )}
        </Map>
      </Box>
    </Card>
  )
}

export default DashboardMap
